import { useState } from 'react';
import { Plus } from 'lucide-react';

import { useToast } from '../../../components/Toast';
import { errorMessage } from '../../../lib/errorMessage';
import { TaskDrawer } from '../tasks/TaskDrawer';
import { TaskList } from '../tasks/TaskList';
import type { TaskRow, TaskStatus } from '../tasks/tasks.schema';
import { useQuickAddTask, useTasks, useUpdateTaskStatus } from '../tasks/tasksQueries';
import type { ProjectRow } from './projects.schema';
import styles from './projects.module.css';

interface Props {
  project: ProjectRow;
  projectOptions: { id: string; name: string }[];
  orgId: string;
  userId: string | null;
}

/** The same task list, drawer and quick-add as /tasks, pinned to one project. */
export function ProjectTasksTab({ project, projectOptions, orgId, userId }: Props) {
  const toast = useToast();
  const [showCompleted, setShowCompleted] = useState(false);
  const {
    data: tasks = [],
    isLoading,
    error,
  } = useTasks(orgId, {
    projectId: project.id,
    showCompleted,
  });
  const quickAdd = useQuickAddTask(orgId, userId);
  const updateStatus = useUpdateTaskStatus(orgId);
  const [title, setTitle] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<TaskRow | null>(null);

  async function handleQuickAdd(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      await quickAdd.mutateAsync({ title: trimmed, project_id: project.id });
      setTitle('');
    } catch (err) {
      toast.error(`לא הצלחנו להוסיף · ${errorMessage(err)}`);
    }
  }

  function handleStatusChange(id: string, status: TaskStatus) {
    updateStatus.mutate(
      { id, status },
      {
        onError: (err) => {
          toast.error(`לא הצלחנו לעדכן · ${errorMessage(err)}`);
        },
      },
    );
  }

  const open = tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length;

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionCardHead}>
        <h3>
          משימות · <bdi>{open}</bdi> פתוחות
        </h3>
        <div className={styles.workspaceActions}>
          <label className={styles.checkboxField}>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => {
                setShowCompleted(e.target.checked);
              }}
            />
            הצג בוצעו/בוטלו
          </label>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              setSelected(null);
              setDrawerOpen(true);
            }}
          >
            <Plus size={14} aria-hidden="true" />
            משימה
          </button>
        </div>
      </div>

      <form
        className={styles.inlineFormActions}
        onSubmit={(e) => {
          void handleQuickAdd(e);
        }}
      >
        <input
          type="text"
          className={styles.input}
          placeholder="הקלד משימה לפרויקט ולחץ Enter…"
          aria-label="הוספה מהירה של משימה לפרויקט"
          value={title}
          disabled={quickAdd.isPending}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
        <button
          type="submit"
          className={styles.btnSecondary}
          disabled={quickAdd.isPending || !title.trim()}
        >
          הוסף
        </button>
      </form>

      {isLoading ? <p className={styles.notice}>טוען משימות...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון משימות</p> : null}
      {!isLoading && !error && tasks.length === 0 ? (
        <p className={styles.notice}>אין משימות בפרויקט. הוסף את הראשונה למעלה.</p>
      ) : null}
      {tasks.length > 0 ? (
        <TaskList
          tasks={tasks}
          projectNameById={{ [project.id]: project.name }}
          onSelect={(task) => {
            setSelected(task);
            setDrawerOpen(true);
          }}
          onChangeStatus={handleStatusChange}
        />
      ) : null}

      <TaskDrawer
        open={drawerOpen}
        task={selected}
        projects={projectOptions}
        defaultProjectId={project.id}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
        }}
      />
    </div>
  );
}
